import {
  Card,
  DatePicker,
  Select,
  Button,
  Checkbox,
  Space,
  message,
  Table,
  Typography,
  Alert,
  Segmented,
  Row,
  Col,
  Divider,
  Tag,
} from 'antd';
import { DownloadOutlined, EyeOutlined, FileTextOutlined } from '@ant-design/icons';
import { useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { fetchPickupAddresses } from '../api/pickup_address.api';
import { listUsersDirectoryAPI } from '../api/auth.api';
import { useAllRegionStores } from '../hooks/useStores';
import {
  downloadShipmentsReportCSV,
  previewShipmentsReport,
  type ShipmentsReportFilters,
} from '../api/reports.api';

const { Title, Text, Paragraph } = Typography;

type ReportType = 'shipment_summary' | 'line_item_detail';

const REPORTS: Record<
  ReportType,
  { apiMode: 'A' | 'B'; label: string; description: string }
> = {
  shipment_summary: {
    apiMode: 'A',
    label: 'Shipment Summary',
    description:
      'One row per shipment. Use for carrier billing reconciliation and postage audits.',
  },
  line_item_detail: {
    apiMode: 'B',
    label: 'Line Item Detail',
    description:
      'One row per SKU per shipment. Use for inventory reconciliation and SKU-level audits.',
  },
};

const fmtDate = (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '');
const fmtDim = (v: number | null) => (v != null ? String(v) : '');

const SHIPMENT_SUMMARY_COLS = [
  {
    title: 'Order Number',
    dataIndex: 'order_number',
    width: 200,
    render: (v: string | null, r: any) => v ?? r.order_id,
  },
  { title: 'Tracking Number', dataIndex: 'tracking_number', width: 180 },
  {
    title: 'Store',
    dataIndex: 'store_name',
    width: 180,
    render: (v: string | null, r: any) => v ?? (r.store_id != null ? `#${r.store_id}` : ''),
  },
  { title: 'Warehouse', dataIndex: 'warehouse_name', width: 160 },
  { title: 'Batch ID', dataIndex: 'batch_id', width: 260 },
  {
    title: 'Order Date',
    dataIndex: 'order_date',
    width: 150,
    render: fmtDate,
  },
  {
    title: 'Label Date',
    dataIndex: 'label_purchase_date',
    width: 150,
    render: fmtDate,
  },
  { title: 'Total Qty', dataIndex: 'total_qty', width: 90, align: 'right' as const },
  { title: 'Weight (lbs)', dataIndex: 'package_weight_lbs', width: 110, align: 'right' as const },
  { title: 'L', dataIndex: 'length_in', width: 70, align: 'right' as const, render: fmtDim },
  { title: 'W', dataIndex: 'width_in', width: 70, align: 'right' as const, render: fmtDim },
  { title: 'H', dataIndex: 'height_in', width: 70, align: 'right' as const, render: fmtDim },
  { title: 'Dim UOM', dataIndex: 'dim_uom', width: 80 },
  { title: 'Zone', dataIndex: 'zone', width: 80 },
  { title: 'Carrier', dataIndex: 'carrier', width: 100 },
  { title: 'Subaccount', dataIndex: 'subaccount', width: 120 },
  { title: 'Rate ($)', dataIndex: 'quoted_rate', width: 90, align: 'right' as const },
  { title: 'Status', dataIndex: 'tracking_status', width: 130 },
  { title: 'Purchased By', dataIndex: 'purchased_by_name', width: 160 },
];

const LINE_ITEM_COLS = [
  {
    title: 'Order Number',
    dataIndex: 'order_number',
    width: 200,
    render: (v: string | null, r: any) => v ?? r.order_id,
  },
  { title: 'Tracking Number', dataIndex: 'tracking_number', width: 180 },
  {
    title: 'Store',
    dataIndex: 'store_name',
    width: 180,
    render: (v: string | null, r: any) => v ?? (r.store_id != null ? `#${r.store_id}` : ''),
  },
  { title: 'Warehouse', dataIndex: 'warehouse_name', width: 160 },
  { title: 'Master SKU', dataIndex: 'sku', width: 140 },
  { title: 'Alias SKU', dataIndex: 'alias_sku', width: 140 },
  { title: 'Item', dataIndex: 'item_name' },
  { title: 'Qty', dataIndex: 'quantity', width: 70, align: 'right' as const },
  { title: 'Batch ID', dataIndex: 'batch_id', width: 260 },
  {
    title: 'Order Date',
    dataIndex: 'order_date',
    width: 150,
    render: fmtDate,
  },
  {
    title: 'Label Date',
    dataIndex: 'label_purchase_date',
    width: 150,
    render: fmtDate,
  },
  { title: 'Weight (lbs)', dataIndex: 'package_weight_lbs', width: 110, align: 'right' as const },
  { title: 'L', dataIndex: 'length_in', width: 70, align: 'right' as const, render: fmtDim },
  { title: 'W', dataIndex: 'width_in', width: 70, align: 'right' as const, render: fmtDim },
  { title: 'H', dataIndex: 'height_in', width: 70, align: 'right' as const, render: fmtDim },
  { title: 'Dim UOM', dataIndex: 'dim_uom', width: 80 },
  { title: 'Zone', dataIndex: 'zone', width: 80 },
  { title: 'Purchased By', dataIndex: 'purchased_by_name', width: 160 },
];

const RANGE_PRESETS: Array<{ label: string; value: [Dayjs, Dayjs] }> = [
  { label: 'Today', value: [dayjs().startOf('day'), dayjs().endOf('day')] },
  { label: 'Last 7 days', value: [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')] },
  { label: 'Last 30 days', value: [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')] },
  { label: 'Month to date', value: [dayjs().startOf('month'), dayjs().endOf('day')] },
  {
    label: 'Last month',
    value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')],
  },
];

export default function Reports() {
  const [reportType, setReportType] = useState<ReportType>('shipment_summary');
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>([
    dayjs().subtract(6, 'day').startOf('day'),
    dayjs().endOf('day'),
  ]);
  const [storeIds, setStoreIds] = useState<number[]>([]);
  const [warehouseIds, setWarehouseIds] = useState<string[]>([]);
  const [createdBy, setCreatedBy] = useState<number[]>([]);
  const [includeVoided, setIncludeVoided] = useState(false);
  const [includeSsShipped, setIncludeSsShipped] = useState(false);
  const [preview, setPreview] = useState<{ rows: any[]; count: number } | null>(null);
  const [busy, setBusy] = useState<'preview' | 'download' | null>(null);

  const stores = useAllRegionStores();
  const { data: pickups = [] } = useQuery<any[]>({
    queryKey: ['pickup-addresses'],
    queryFn: fetchPickupAddresses,
    staleTime: 5 * 60_000,
  });
  const { data: users = [] } = useQuery<any[]>({
    queryKey: ['users-directory'],
    queryFn: listUsersDirectoryAPI,
    staleTime: 5 * 60_000,
  });

  const shipFromOptions = useMemo(
    () =>
      pickups
        .filter((p) => p.ss_warehouse_id != null && p.ss_warehouse_id !== '')
        .map((p) => ({ label: p.name, value: String(p.ss_warehouse_id) })),
    [pickups],
  );

  const storeOptions = useMemo(
    () => stores.map((s: any) => ({ label: s.storeName, value: Number(s.storeId) })),
    [stores],
  );

  const userOptions = useMemo(
    () =>
      users.map((u: any) => {
        const name = `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim();
        return { label: name ? `${name} (${u.email})` : u.email, value: Number(u.id) };
      }),
    [users],
  );

  const buildFilters = (): ShipmentsReportFilters | null => {
    if (!range?.[0] || !range?.[1]) {
      message.error('Select a date range');
      return null;
    }
    return {
      mode: REPORTS[reportType].apiMode,
      from: range[0].format('YYYY-MM-DD'),
      to: range[1].format('YYYY-MM-DD'),
      storeIds,
      warehouseIds,
      createdBy,
      includeVoided,
      includeSsShipped,
    };
  };

  const onPreview = async () => {
    const f = buildFilters();
    if (!f) return;
    setBusy('preview');
    try {
      const r = await previewShipmentsReport(f);
      setPreview(r);
    } catch (err) {
      message.error((err as Error).message ?? 'Preview failed');
    } finally {
      setBusy(null);
    }
  };

  const onDownload = async () => {
    const f = buildFilters();
    if (!f) return;
    setBusy('download');
    try {
      await downloadShipmentsReportCSV(f);
      message.success('CSV downloaded');
    } catch (err) {
      message.error((err as Error).message ?? 'Download failed');
    } finally {
      setBusy(null);
    }
  };

  const activeFilterCount =
    (storeIds.length > 0 ? 1 : 0) +
    (warehouseIds.length > 0 ? 1 : 0) +
    (createdBy.length > 0 ? 1 : 0) +
    (includeVoided ? 1 : 0) +
    (includeSsShipped ? 1 : 0);

  const currentCols = reportType === 'shipment_summary' ? SHIPMENT_SUMMARY_COLS : LINE_ITEM_COLS;

  return (
    <div style={{ padding: '16px 24px', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>
          <FileTextOutlined style={{ marginRight: 8, color: '#1677ff' }} />
          Reports
        </Title>
        <Text type="secondary">
          Export shipment and item-level data for billing, inventory, and audit workflows.
        </Text>
      </div>

      <Card
        style={{ marginBottom: 16 }}
        styles={{ body: { padding: 20 } }}
      >
        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Report Type
          </Text>
          <Segmented<ReportType>
            value={reportType}
            onChange={(v) => {
              setReportType(v);
              setPreview(null);
            }}
            options={[
              { label: 'Shipment Summary', value: 'shipment_summary' },
              { label: 'Line Item Detail', value: 'line_item_detail' },
            ]}
          />
          <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 13 }}>
            {REPORTS[reportType].description}
          </Paragraph>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>
              Label Purchase Date
            </Text>
            <DatePicker.RangePicker
              value={range ?? undefined}
              onChange={(v) => setRange(v as [Dayjs, Dayjs] | null)}
              style={{ width: '100%' }}
              presets={RANGE_PRESETS}
              allowClear={false}
            />
          </Col>
          <Col xs={24} md={12}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>
              Brand / Store
            </Text>
            <Select
              mode="multiple"
              allowClear
              placeholder="All brands"
              style={{ width: '100%' }}
              value={storeIds}
              onChange={setStoreIds}
              options={storeOptions}
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
            />
          </Col>
          <Col xs={24} md={12}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>
              Ship From
            </Text>
            <Select
              mode="multiple"
              allowClear
              placeholder="All warehouses"
              style={{ width: '100%' }}
              value={warehouseIds}
              onChange={setWarehouseIds}
              options={shipFromOptions}
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
            />
          </Col>
          <Col xs={24} md={12}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>
              Purchased By
            </Text>
            <Select
              mode="multiple"
              allowClear
              placeholder="Anyone"
              style={{ width: '100%' }}
              value={createdBy}
              onChange={setCreatedBy}
              options={userOptions}
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
            />
          </Col>
        </Row>

        <Divider style={{ margin: '16px 0' }} />

        <Row gutter={[16, 8]} align="middle">
          <Col flex="auto">
            <Space size="large" wrap>
              <Checkbox
                checked={includeVoided}
                onChange={(e) => setIncludeVoided(e.target.checked)}
              >
                Include voided shipments
              </Checkbox>
              <Checkbox
                checked={includeSsShipped}
                onChange={(e) => setIncludeSsShipped(e.target.checked)}
              >
                Include ShipStation-shipped orders (non-UniUni carriers)
              </Checkbox>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<EyeOutlined />} onClick={onPreview} loading={busy === 'preview'}>
                Preview
              </Button>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={onDownload}
                loading={busy === 'download'}
              >
                Download CSV
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {preview && (
        <Card
          title={
            <Space>
              <span>Preview</span>
              <Tag color="blue">{REPORTS[reportType].label}</Tag>
              <Tag>
                {preview.count.toLocaleString()} {preview.count === 1 ? 'row' : 'rows'}
              </Tag>
              {activeFilterCount > 0 && (
                <Tag color="geekblue">
                  {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'} applied
                </Tag>
              )}
            </Space>
          }
          styles={{ body: { padding: preview.count === 0 ? 20 : 0 } }}
        >
          {preview.count === 0 ? (
            <Alert
              type="info"
              showIcon
              message="No shipments match the selected filters"
              description="Try widening the date range or clearing filters."
            />
          ) : (
            <>
              {preview.count > 500 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ margin: 12 }}
                  message={`Showing first 500 of ${preview.count.toLocaleString()} rows`}
                  description="Download the CSV for the full dataset."
                />
              )}
              <Table
                size="small"
                pagination={{ pageSize: 50, size: 'small', showSizeChanger: false }}
                rowKey={(_r, i) => String(i)}
                scroll={{ x: 'max-content' }}
                dataSource={preview.rows.slice(0, 500)}
                columns={currentCols}
              />
            </>
          )}
        </Card>
      )}
    </div>
  );
}
